import React, { useContext, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Typography, Menu, MenuItem, ListItemIcon, Fade, Divider } from "@material-ui/core";
import DownloadXmlIcon from "@material-ui/icons/CodeTwoTone";
import DownloadPdfIcon from "@material-ui/icons/PictureAsPdfTwoTone";
import PaymentsIcon from "@material-ui/icons/AccountBalanceWalletTwoTone";
import NewPaymentIcon from "@material-ui/icons/PlaylistAddTwoTone";
import CancelTwoToneIcon from "@material-ui/icons/CancelTwoTone";
import ReplaceIcon from "@material-ui/icons/RestorePageTwoTone";
import CustomerCfdisIcon from "@material-ui/icons/ArchiveTwoTone";
import CustomerBalanceIcon from "@material-ui/icons/AccountBalanceWalletTwoTone";
import MarkAsPaidIcon from "@material-ui/icons/BeenhereTwoTone";

import translate from "../i18n/Translator";
import { Cfdi } from "../model/Cfdi";
import { AppContext } from "../context/AppContext";
import { getCfdiXml, getCfdiPdf } from "../api/CfdiAPI";
import { getCfdiXml as getCustomerCfdiXml, getCfdiPdf as getCustomerCfdiPdf } from "../api/CustomerCfdiAPI";

interface KeyPairMenuProps {
    cfdi: Cfdi;
    customerId?: string;
    anchor: HTMLElement | null;
    onClose(): any;
    onNewPayment(): any;
    onCancel(): any;
    onReplace(): any;
    onShowCustomerCfdis(): any;
    onGetBalance(): any;
    onMarkAsPaid(): any;
}

export default function CfdiMenu(props: KeyPairMenuProps) {
    const context = useContext(AppContext);
    const [pdfUrl, setPdfUrl] = useState<string>();
    const [xmlUrl, setXmlUrl] = useState<string>();

    useEffect(() => {
        if (!context.isGrantedAny(["CfdisRead", "CustomersCfdisRead"])) {
            return;
        }

        if (props.cfdi.metadata && props.cfdi.metadata.xml_path) {
            if (props.customerId) {
                getCustomerCfdiXml(context.session!.tenant!.id, props.customerId, props.cfdi.id).then(xmlUrl => {
                    setXmlUrl(xmlUrl);
                });
            } else {
                getCfdiXml(context.session!.tenant!.id, props.cfdi.id).then(xmlUrl => {
                    setXmlUrl(xmlUrl);
                });
            }
        }
        if (props.cfdi.metadata && props.cfdi.metadata.pdf_path) {
            if (props.customerId) {
                getCustomerCfdiPdf(context.session!.tenant!.id, props.customerId, props.cfdi.id).then(pdfUrl => {
                    setPdfUrl(pdfUrl);
                });
            } else {
                getCfdiPdf(context.session!.tenant!.id, props.cfdi.id).then(pdfUrl => {
                    setPdfUrl(pdfUrl);
                });
            }
        }
        // eslint-disable-next-line
    }, [props.customerId, props.cfdi.id]);

    return (
        <Menu
            id="tenants-item"
            anchorEl={props.anchor}
            keepMounted
            onClose={props.onClose}
            open
            TransitionComponent={Fade}
        >
            {context.isGranted("CfdisRead") && !props.customerId && (
                <MenuItem button onClick={props.onShowCustomerCfdis} dense>
                    <ListItemIcon>
                        <CustomerCfdisIcon fontSize="small" color="primary" />
                    </ListItemIcon>
                    <Typography variant="inherit">{translate("buttons.show_customer_cfdis")}</Typography>
                </MenuItem>
            )}
            {context.isGranted("CfdisRead") && (
                <MenuItem button onClick={props.onGetBalance} dense>
                    <ListItemIcon>
                        <CustomerBalanceIcon fontSize="small" color="primary" />
                    </ListItemIcon>
                    <Typography variant="inherit">{translate("buttons.get_customer_balance")}</Typography>
                </MenuItem>
            )}
            {context.isGranted("CfdisRead") && (
                <Divider />
            )}
            {context.isGranted("CfdisUpdate") && props.cfdi.metadata && props.cfdi.metadata.type === 'I' && props.cfdi.metadata.payment_status === 'pending' && (
                <MenuItem button onClick={props.onMarkAsPaid} dense>
                    <ListItemIcon>
                        <MarkAsPaidIcon fontSize="small" color="primary" />
                    </ListItemIcon>
                    <Typography variant="inherit">{translate("buttons.mark_as_paid")}</Typography>
                </MenuItem>
            )}
            <MenuItem button onClick={props.onClose} dense component="a" href={xmlUrl || "#"} disabled={!xmlUrl} target="_blank" rel="noopener noreferrer">
                <ListItemIcon>
                    <DownloadXmlIcon fontSize="small" color="primary" />
                </ListItemIcon>
                <Typography variant="inherit">{translate("buttons.download_xml")}</Typography>
            </MenuItem>
            <MenuItem button onClick={props.onClose} dense component="a" href={pdfUrl || "#"} disabled={!pdfUrl} target="_blank" rel="noopener noreferrer">
                <ListItemIcon>
                    <DownloadPdfIcon fontSize="small" color="primary" />
                </ListItemIcon>
                <Typography variant="inherit">{translate("buttons.download_pdf")}</Typography>
            </MenuItem>
            {context.isGranted("CustomersPaymentsRead") && props.cfdi.metadata && props.cfdi.metadata.payment_id && (
                <MenuItem button component={Link} to={`/payments/${props.cfdi.metadata.payment_id}`} onClick={props.onClose} dense>
                    <ListItemIcon>
                        <PaymentsIcon fontSize="small" color="primary" />
                    </ListItemIcon>
                    <Typography variant="inherit">{translate("buttons.see_payment")}</Typography>
                </MenuItem>
            )}
            {context.isGranted("CustomersPaymentsCreate") && props.cfdi.metadata && !props.cfdi.metadata.payment_id && (
                <MenuItem button onClick={props.onNewPayment} dense>
                    <ListItemIcon>
                        <NewPaymentIcon fontSize="small" color="primary" />
                    </ListItemIcon>
                    <Typography variant="inherit">{translate("buttons.new_payment")}</Typography>
                </MenuItem>
            )}
            {context.isGranted("CfdisDelete") && props.cfdi.metadata && props.cfdi.metadata.payment_status !== "cancelled" && props.cfdi.tipo_comprobante === "P" && (
                <MenuItem button onClick={props.onReplace} dense>
                    <ListItemIcon>
                        <ReplaceIcon fontSize="small" color="primary" />
                    </ListItemIcon>
                    <Typography variant="inherit">{translate("buttons.replace")}</Typography>
                </MenuItem>
            )}
            {context.isGranted("CfdisDelete") && props.cfdi.metadata && props.cfdi.metadata.payment_status !== "cancelled" && (
                <MenuItem button onClick={props.onCancel} dense>
                    <ListItemIcon>
                        <CancelTwoToneIcon fontSize="small" color="error" />
                    </ListItemIcon>
                    <Typography variant="inherit">{translate("buttons.cancel")}</Typography>
                </MenuItem>
            )}
        </Menu>
    );

}